import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PaymentsSettings from "@/components/PaymentsSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Plus, Trash2, AlertTriangle, CheckCircle2, Warehouse, Tag } from "lucide-react";
import { toast } from "sonner";
import { CatalogServicesTab, ScreenAccountsTab } from "@/components/settings/AccountsTabs";
import { ModulesTab, CommercialTab, DocumentsTab } from "@/components/settings/ConfigTabs";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { DEFAULT_CATEGORIES } from "@/components/stock/constants";
import { Switch } from "@/components/ui/switch";
import { ColorSwatches } from "@/lib/categoryColors";
import { useUnsavedGuard } from "@/context/UnsavedChanges";

export default function SettingsPage({ initialTab, showTabList = true }) {
  const { org, refreshOrg } = useAuth();
  const sp = new URLSearchParams(useLocation().search);
  const defaultTab = initialTab || ((sp.get("connect") || sp.get("sub") || sp.get("tab") === "payments") ? "payments" : "org");
  const [tab, setTab] = useState(defaultTab);
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  const [form, setForm] = useState(org || {});

  useEffect(() => { setForm(org || {}); }, [org]);

  const wNewId = () => Math.random().toString(36).slice(2, 10);
  const addWarehouse = () => setForm((f) => ({ ...f, warehouses: [...(f.warehouses || []), { id: wNewId(), name: "", address: "" }] }));
  const updateWarehouse = (id, k, v) => setForm((f) => ({ ...f, warehouses: (f.warehouses || []).map((w) => (w.id === id ? { ...w, [k]: v } : w)) }));
  const removeWarehouse = (id) => setForm((f) => ({ ...f, warehouses: (f.warehouses || []).filter((w) => w.id !== id) }));

  const addCategory = () => setForm((f) => ({ ...f, catalog_categories: [...(f.catalog_categories || []), ""] }));
  const updateCategory = (i, v) => setForm((f) => ({ ...f, catalog_categories: (f.catalog_categories || []).map((c, idx) => (idx === i ? v : c)) }));
  const removeCategory = (i) => setForm((f) => ({ ...f, catalog_categories: (f.catalog_categories || []).filter((_, idx) => idx !== i) }));
  const loadDefaultCategories = () => setForm((f) => ({ ...f, catalog_categories: [...DEFAULT_CATEGORIES] }));

  const saveOrg = async () => {
    const payload = {
      name: form.name, brand_color: form.brand_color, fiscal_status: form.fiscal_status,
      siret: form.siret, address: form.address, modules: form.modules, logo: form.logo || "",
      brand_website: form.brand_website || "",
      deposit_percent: parseFloat(form.deposit_percent), balance_days_before: parseInt(form.balance_days_before),
      cancellation_policy: form.cancellation_policy,
      brand_tagline: form.brand_tagline || "", contact_email: form.contact_email || "", phone: form.phone || "",
      vat_number: form.vat_number || "", share_capital: form.share_capital || "",
      vat_registered: !!form.vat_registered, tax_rate_default: parseFloat(form.tax_rate_default) || 0,
      social_charges_rate: Number.isFinite(parseFloat(form.social_charges_rate)) ? parseFloat(form.social_charges_rate) : undefined,
      legal_mentions: form.legal_mentions || "", iban: form.iban || "", bic: form.bic || "",
      notif_channel: form.notif_channel || "both",
      pb_reminder_days: Number.isFinite(parseInt(form.pb_reminder_days)) ? parseInt(form.pb_reminder_days) : 3,
      warehouses: (form.warehouses || []).filter((w) => (w.name || "").trim()).map((w) => ({ id: w.id || wNewId(), name: w.name.trim(), address: (w.address || "").trim() })),
      catalog_categories: (form.catalog_categories || []).map((c) => (c || "").trim()).filter(Boolean),
      category_colors_enabled: !!form.category_colors_enabled,
      category_colors: Object.fromEntries(Object.entries(form.category_colors || {}).filter(([, v]) => v)),
      block_pack_insufficient_stock: !!form.block_pack_insufficient_stock,
      document_template: form.document_template || undefined,
    };
    try {
      const { data } = await api.put("/org", payload);
      // Vérification réelle : on confronte la réponse persistée (état en base) à ce qui a été envoyé.
      const mismatches = Object.keys(payload).filter((k) => {
        if (payload[k] === undefined || k === "document_template" || k === "modules" || k === "warehouses" || k === "catalog_categories" || k === "category_colors") return false;
        const sent = typeof payload[k] === "number" ? payload[k] : (payload[k] ?? "");
        const saved = typeof payload[k] === "number" ? (data?.[k] ?? null) : (data?.[k] ?? "");
        return String(sent) !== String(saved);
      });
      if (mismatches.length > 0) {
        toast.error(`Enregistrement incomplet : ${mismatches.join(", ")} non sauvegardé(s). Réessayez.`);
        return;
      }
      refreshOrg(data);
      setForm(data);
      toast.success("Enregistré ✓ — vos modifications sont bien sauvegardées", {
        icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement — vos modifications n'ont PAS été sauvegardées.");
    }
  };

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Image trop lourde (max 4 Mo)");
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 400;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setForm((f) => ({ ...f, logo: canvas.toDataURL("image/png") }));
        toast.success("Logo chargé — cliquez sur Enregistrer");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const TABS = [
    { v: "org", l: "Organisation" },
    { v: "modules", l: "Modules", testid: "tab-modules" },
    { v: "commercial", l: "Commercial" },
    { v: "payments", l: "Paiements", testid: "tab-payments" },
    { v: "documents", l: "Documents", testid: "tab-documents" },
    { v: "screen", l: "Écran", testid: "tab-screen" },
  ];

  const SAVED_FIELDS = ["name", "brand_color", "fiscal_status", "siret", "address", "logo", "brand_website", "deposit_percent", "balance_days_before", "cancellation_policy", "brand_tagline", "contact_email", "phone", "vat_number", "share_capital", "vat_registered", "tax_rate_default", "social_charges_rate", "legal_mentions", "iban", "bic", "notif_channel", "pb_reminder_days", "document_template", "modules", "warehouses"];
  const snap = (o) => JSON.stringify(SAVED_FIELDS.map((k) => o?.[k] ?? null));
  const dirty = !!org && snap(form) !== snap(org);
  useUnsavedGuard(dirty);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  return (
    <>
      {dirty && (
        <div data-testid="unsaved-banner" className="sticky top-0 z-30 mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#FFD500]/40 bg-[#1a1710] px-4 py-2.5 text-sm text-[#FFD500] shadow-lg">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />Modifications non enregistrées — pensez à cliquer sur « Enregistrer » avant de quitter la page.</span>
          <Button data-testid="unsaved-save" onClick={saveOrg} className="h-8 shrink-0 rounded-full bg-[#FF6A00] px-4 text-xs font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1 h-3.5 w-3.5" />Enregistrer</Button>
        </div>
      )}
    <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="flex h-full flex-col gap-4 md:flex-row md:gap-6">
      {showTabList && (
      <div className="shrink-0 md:w-52">
        <h1 className="mb-4 hidden font-display text-2xl font-bold text-white md:block">Paramètres</h1>
        <TabsList className="flex h-auto w-full gap-1 overflow-x-auto rounded-2xl bg-zinc-900 p-1 md:flex-col md:overflow-visible">
          {TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={t.testid}
              className="w-full shrink-0 justify-start rounded-xl px-4 py-2 text-sm text-zinc-400 data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white">
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1 md:pr-2">

        <TabsContent value="org" className="mt-0">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="xl:col-span-2 md:col-span-2"><Label className="text-zinc-400">Nom commercial</Label><Input className="mt-1 border-zinc-800 bg-zinc-950" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="flex items-center gap-3"><div><Label className="text-zinc-400">Couleur de marque</Label><div className="mt-1 flex items-center gap-3"><input type="color" value={form.brand_color || "#FF6A00"} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-16 rounded-lg border border-zinc-800 bg-zinc-950" /><span className="text-sm text-zinc-500">{form.brand_color}</span></div></div></div>
            <div>
              <Label className="text-zinc-400">Logo</Label>
              <p className="mb-2 text-xs text-zinc-500">Affiché en en-tête de vos devis et factures. PNG transparent recommandé.</p>
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-zinc-800 bg-white">
                  {form.logo ? <img data-testid="logo-preview" src={form.logo} alt="logo" className="max-h-16 max-w-16 object-contain" /> : <span className="text-2xl font-bold text-zinc-400">{(form.name || "E").slice(0, 1)}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer rounded-full border border-zinc-700 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800">
                    <input data-testid="logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogo} />
                    {form.logo ? "Changer le logo" : "Choisir un logo"}
                  </label>
                  {form.logo && <button data-testid="logo-remove" onClick={() => setForm({ ...form, logo: "" })} className="text-xs text-zinc-500 hover:text-red-400">Retirer le logo</button>}
                </div>
              </div>
            </div>
            <div><Label className="text-zinc-400">Adresse</Label><AddressAutocomplete testid="org-address" className="mt-1 border-zinc-800 bg-zinc-950" value={form.address || ""} onChange={(v) => setForm((f) => ({ ...f, address: v }))} /></div>
            <div><Label className="text-zinc-400">SIRET</Label><Input className="mt-1 border-zinc-800 bg-zinc-950" value={form.siret || ""} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-400">Email de contact</Label><Input data-testid="org-contact-email" type="email" className="mt-1 border-zinc-800 bg-zinc-950" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label className="text-zinc-400">Téléphone</Label><Input data-testid="org-phone" className="mt-1 border-zinc-800 bg-zinc-950" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label className="text-zinc-400">Slogan</Label><Input data-testid="org-tagline" className="mt-1 border-zinc-800 bg-zinc-950" placeholder="Votre accroche" value={form.brand_tagline || ""} onChange={(e) => setForm({ ...form, brand_tagline: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Statut</Label>
                <Select value={form.fiscal_status || "auto-entrepreneur"} onValueChange={(v) => setForm({ ...form, fiscal_status: v })}>
                  <SelectTrigger data-testid="org-fiscal" className="mt-1 border-zinc-800 bg-zinc-950"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900"><SelectItem value="auto-entrepreneur">Auto-entrepreneur</SelectItem><SelectItem value="societe">Société</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400">N° TVA</Label><Input data-testid="org-vat" className="mt-1 border-zinc-800 bg-zinc-950" value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
            </div>
            {form.fiscal_status === "societe" && (
              <div><Label className="text-zinc-400">Capital social</Label><Input data-testid="org-capital" placeholder="ex : 10 000 €" className="mt-1 border-zinc-800 bg-zinc-950" value={form.share_capital || ""} onChange={(e) => setForm({ ...form, share_capital: e.target.value })} /></div>
            )}
            <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
              <label className="flex cursor-pointer items-center justify-between text-sm text-zinc-300"><span>Assujetti à la TVA ?</span><input type="checkbox" data-testid="org-vat-registered" checked={!!form.vat_registered} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} className="h-4 w-4 accent-[#FF6A00]" /></label>
              {form.vat_registered && <div className="mt-2"><Label className="text-zinc-400">Taux de TVA par défaut (%)</Label><Input data-testid="org-tax-rate" type="number" className="mt-1 border-zinc-800 bg-zinc-950" value={form.tax_rate_default ?? 20} onChange={(e) => setForm({ ...form, tax_rate_default: e.target.value })} /></div>}
            </div>
            <div>
              <Label className="text-zinc-400">Taux de cotisations sociales / URSSAF (%)</Label>
              <Input data-testid="org-social-rate" type="number" step="0.1" className="mt-1 border-zinc-800 bg-zinc-950" value={form.social_charges_rate ?? (form.fiscal_status === "societe" ? 0 : 21.2)} onChange={(e) => setForm({ ...form, social_charges_rate: e.target.value })} />
              <p className="mt-1 text-xs text-zinc-500">Sert à estimer vos charges dans la page Comptabilité. Auto-entrepreneur prestations de services ≈ 21,2 %. Société : laissez 0 si non applicable.</p>
            </div>
            <div>
              <Label className="text-zinc-400">Adresse web (affichée à vos clients)</Label>
              <Input data-testid="org-website" className="mt-1 border-zinc-800 bg-zinc-950" placeholder="ex : acme-events.fr" value={form.brand_website || ""} onChange={(e) => setForm({ ...form, brand_website: e.target.value })} />
              <p className="mt-1 text-xs text-zinc-500">Affichée sur le portail client (en-tête & pied de page) et dans vos emails, à la place de « Propulsé par HalgarOS ».</p>
            </div>
            <div>
              <Label className="text-zinc-400">Notifications</Label>
              <p className="mb-1 text-xs text-zinc-500">Comment être prévenu de l'activité (validation de design par un client, etc.).</p>
              <Select value={form.notif_channel || "both"} onValueChange={(v) => setForm({ ...form, notif_channel: v })}>
                <SelectTrigger data-testid="notif-channel" className="mt-1 border-zinc-800 bg-zinc-950"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="both">Email + notification dans l'app</SelectItem>
                  <SelectItem value="app">Notification dans l'app uniquement</SelectItem>
                  <SelectItem value="email">Email uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid="warehouses-section">
                <div className="mb-1 flex items-center gap-2"><Warehouse className="h-4 w-4 text-[#FF6A00]" /><Label className="text-zinc-300">Entrepôts / Zones de stockage</Label></div>
                <p className="mb-3 text-xs text-zinc-500">Nom et adresse des lieux où votre matériel est stocké. Vous pourrez affecter chaque matériel à un entrepôt lors de sa création dans le catalogue.</p>
                <div className="space-y-2">
                  {(form.warehouses || []).map((w, i) => (
                    <div key={w.id} data-testid={`warehouse-row-${i}`} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.5fr_auto]">
                      <Input data-testid={`warehouse-name-${i}`} placeholder="Nom (ex: Entrepôt principal)" className="border-zinc-800 bg-zinc-900" value={w.name || ""} onChange={(e) => updateWarehouse(w.id, "name", e.target.value)} />
                      <Input data-testid={`warehouse-address-${i}`} placeholder="Adresse" className="border-zinc-800 bg-zinc-900" value={w.address || ""} onChange={(e) => updateWarehouse(w.id, "address", e.target.value)} />
                      <button data-testid={`warehouse-del-${i}`} onClick={() => removeWarehouse(w.id)} className="flex items-center justify-center rounded-lg border border-zinc-800 px-3 text-zinc-500 hover:border-red-500/40 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {(form.warehouses || []).length === 0 && <p className="rounded-lg border border-dashed border-zinc-800 p-3 text-center text-xs text-zinc-500">Aucun entrepôt enregistré.</p>}
                </div>
                <Button data-testid="add-warehouse" variant="outline" onClick={addWarehouse} className="mt-3 rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Plus className="mr-1 h-4 w-4" />Ajouter un entrepôt</Button>
              </div>
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid="categories-section">
                <div className="mb-1 flex items-center gap-2"><Tag className="h-4 w-4 text-[#FF6A00]" /><Label className="text-zinc-300">Catégories du catalogue</Label></div>
                <p className="mb-3 text-xs text-zinc-500">Son, lumière, mobilier… Ces catégories sont proposées lors de la création d'un article dans Catalogue & Matériel.</p>
                <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3" data-testid="category-colors-toggle-row">
                  <Switch data-testid="category-colors-toggle" checked={!!form.category_colors_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, category_colors_enabled: v }))} />
                  <span><span className="block text-sm text-zinc-200">Couleurs de catégories</span><span className="block text-xs text-zinc-500">Affichées sur les cartes du catalogue et dans l'éditeur de devis, uniquement pour votre équipe (jamais sur les documents clients).</span></span>
                </label>
                <div className="space-y-2">
                  {(form.catalog_categories || []).map((c, i) => (
                    <div key={i} data-testid={`category-row-${i}`} className="space-y-2">
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input data-testid={`category-name-${i}`} placeholder="Nom de la catégorie (ex: Son)" className="border-zinc-800 bg-zinc-900" value={c || ""} onChange={(e) => updateCategory(i, e.target.value)} />
                      <button data-testid={`category-del-${i}`} onClick={() => removeCategory(i)} className="flex items-center justify-center rounded-lg border border-zinc-800 px-3 text-zinc-500 hover:border-red-500/40 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    {form.category_colors_enabled && (c || "").trim() && (
                      <ColorSwatches testid={`category-color-${i}`} value={(form.category_colors || {})[(c || "").trim()] || null}
                        onChange={(v) => setForm((f) => ({ ...f, category_colors: { ...(f.category_colors || {}), [(c || "").trim()]: v } }))} />
                    )}
                    </div>
                  ))}
                  {(form.catalog_categories || []).length === 0 && (
                    <div className="rounded-lg border border-dashed border-zinc-800 p-3 text-center text-xs text-zinc-500">
                      Aucune catégorie personnalisée. Les catégories par défaut ({DEFAULT_CATEGORIES.join(", ")}) sont utilisées.
                    </div>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button data-testid="add-category" variant="outline" onClick={addCategory} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Plus className="mr-1 h-4 w-4" />Ajouter une catégorie</Button>
                  {(form.catalog_categories || []).length === 0 && (
                    <Button data-testid="load-default-categories" variant="outline" onClick={loadDefaultCategories} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">Charger les catégories par défaut</Button>
                  )}
                </div>
              </div>
            </div>
            <div className="md:col-span-2 xl:col-span-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid="block-pack-toggle-wrap">
                <input type="checkbox" data-testid="block-pack-toggle" checked={!!form.block_pack_insufficient_stock} onChange={(e) => setForm((f) => ({ ...f, block_pack_insufficient_stock: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-[#FF6A00]" />
                <span>
                  <span className="block text-sm font-medium text-zinc-200">Bloquer la création d'un pack si le stock est insuffisant</span>
                  <span className="mt-0.5 block text-xs text-zinc-500">Si activé, impossible d'enregistrer un pack demandant plus d'unités d'un matériel que le stock disponible (ex : 3 packs de 2 enceintes = 6 enceintes requises).</span>
                </span>
              </label>
            </div>
            <Button data-testid="save-org" onClick={saveOrg} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1 h-4 w-4" />Enregistrer</Button>
          </div>
          </div>
        </TabsContent>

        <TabsContent value="modules" className="mt-0">
          <ModulesTab form={form} setForm={setForm} access={org?.access} onSave={saveOrg} onSeePlans={() => setTab("payments")} />
        </TabsContent>

        <TabsContent value="catalog" className="mt-0">
          <CatalogServicesTab />
        </TabsContent>

        <TabsContent value="commercial" className="mt-0">
          <CommercialTab form={form} setForm={setForm} onSave={saveOrg} />
        </TabsContent>

        <TabsContent value="payments" className="mt-0">
          <PaymentsSettings />
        </TabsContent>

        <TabsContent value="screen" className="mt-0">
          <ScreenAccountsTab />
        </TabsContent>

        <TabsContent value="documents" className="mt-0">
          <DocumentsTab form={form} setForm={setForm} onSave={saveOrg} />
        </TabsContent>
      </div>
    </Tabs>
    </>
  );
}
     